import { useState, useEffect, useMemo } from "react";
import { onValue, ref } from "firebase/database";
import {
  currentEpoch,
  gaugeTotals,
  userOptOutGaugesAtEpoch,
  withheldWeightsAtEpoch,
} from "../../../scripts/scripts";

function getRound(market) {
  let round = Math.floor(Math.floor(Date.now() / 1000) / (86400 * 14)) - 1348;
  if (
    (round + 1348) * 86400 * 14 + 60 * 60 * 24 * 5 <
    Math.floor(Date.now() / 1000)
  ) {
    round++;
  }
  if (market === "fxn") round -= 65;
  if (market === "yb") {
    round = Math.floor(Math.floor(Date.now() / 1000) / (86400 * 7)) - 2910;
  }
  return round;
}

function getDbMarket(market) {
  if (market === "fxn") return "v2fxn";
  if (market === "yb") return "v2yb";
  return "v2";
}

export function useBribesData(db, market, walletAddress, proposal, provider) {
  const [bribesData, setBribesData] = useState({});
  const [snapshotVotes, setSnapshotVotes] = useState({});
  const [tokenPriceValue, setTokenPriceValue] = useState({});
  const [liveVotesByGauge, setLiveVotesByGauge] = useState(null);
  const [optedOutGauges, setOptedOutGauges] = useState(() => new Set());

  const round = getRound(market);

  const currentUser = useMemo(
    () => (walletAddress ? { uid: walletAddress } : null),
    [walletAddress]
  );

  // Gauge addresses come straight from the deposits (uiStats keys). The legacy
  // snapshot "choices" index list is no longer used — we only care about gauges
  // that actually have deposits, and their addresses are enough to query the
  // on-chain gaugeTotals.
  const gaugeAddresses = useMemo(
    () => Object.keys(bribesData).filter((g) => /^0x[a-fA-F0-9]{40}$/.test(g)),
    [bribesData]
  );

  useEffect(() => {
    if (!db) return;

    setBribesData({});
    setLiveVotesByGauge(null);

    const dbMarket = getDbMarket(market);

    const uiStatsRef = ref(db, dbMarket + "/uiStats/" + round);
    const unsubUiStats = onValue(uiStatsRef, (snapshot) => {
      const data = snapshot.val();
      if (data != null) setBribesData(data);
    });

    const tokenPriceRef = ref(db, "api/tokens");
    const unsubPrices = onValue(tokenPriceRef, (snapshot) => {
      const data = snapshot.val();
      if (data != null) setTokenPriceValue(data);
    });

    let unsubVotes = () => {};
    if (currentUser?.uid) {
      const snapshotVoteRef = ref(
        db,
        dbMarket + "/snapshotVoteData/" + round + "/votes/gauges"
      );
      unsubVotes = onValue(snapshotVoteRef, (snapshot) => {
        const data = snapshot.val();
        if (data != null) setSnapshotVotes(data);
      });
    }

    return () => {
      unsubUiStats();
      unsubPrices();
      unsubVotes();
    };
  }, [db, market, round, currentUser]);

  // Live gauge totals from Convex GaugeVoting (replaces Snapshot proposal.scores)
  useEffect(() => {
    let cancelled = false;

    if (
      !proposal?.platform ||
      proposal.id == null ||
      !provider ||
      !gaugeAddresses.length
    ) {
      setLiveVotesByGauge(null);
      return;
    }

    (async () => {
      const totals = await gaugeTotals(
        proposal.platform,
        proposal.id,
        gaugeAddresses,
        provider
      );
      if (cancelled) return;
      if (totals && Object.keys(totals).length > 0) {
        // vlCVX opt-outs withhold vote weight from reward math. Curve and
        // f(x) each have their own OptOutRegistry; yb does not.
        let withheld = {};
        const optOutMarket =
          market === "fxn" ? "fxn" : market === "yb" ? null : "curve";
        if (optOutMarket) {
          const epoch =
            proposal.epoch != null ? proposal.epoch : currentEpoch();
          withheld = await withheldWeightsAtEpoch(
            gaugeAddresses,
            epoch,
            provider,
            optOutMarket
          );
        }
        if (cancelled) return;
        const byGauge = {};
        gaugeAddresses.forEach((gaugeAddr) => {
          const key = gaugeAddr.toLowerCase();
          const raw = totals[key] ?? totals[gaugeAddr] ?? 0;
          const held = withheld[key] ?? withheld[gaugeAddr] ?? 0;
          byGauge[key] = Math.max(0, raw - held);
        });
        setLiveVotesByGauge(byGauge);
      } else {
        setLiveVotesByGauge(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proposal, provider, gaugeAddresses, market]);

  // Gauges this wallet has opted out of for the epoch this round pays on.
  // A next-epoch opt-out does not remove eligibility until that epoch begins.
  useEffect(() => {
    let cancelled = false;
    const optOutMarket =
      market === "fxn" ? "fxn" : market === "yb" ? null : "curve";
    if (!optOutMarket || !walletAddress || !provider) {
      setOptedOutGauges(new Set());
      return;
    }
    const epoch = proposal?.epoch != null ? proposal.epoch : currentEpoch();
    (async () => {
      const gauges = await userOptOutGaugesAtEpoch(
        walletAddress,
        epoch,
        provider,
        optOutMarket
      );
      if (cancelled) return;
      setOptedOutGauges(
        new Set((gauges || []).map((gauge) => gauge.toLowerCase()))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [walletAddress, provider, market, proposal]);

  const bribesArray = useMemo(() => {
    if (Object.keys(bribesData).length === 0) return [];

    const bribesFormatted = [];

    Object.keys(bribesData).forEach((gauge) => {
      const gaugeData = bribesData[gauge];
      if (!gaugeData) return;

      const liveVotes = liveVotesByGauge
        ? liveVotesByGauge[gauge.toLowerCase()] ?? 0
        : gaugeData.votes || 0;
      const totalUsd = gaugeData.totalUsd || 0;

      const optedOut = optedOutGauges.has(gauge.toLowerCase());
      const bribeForDisplay = {
        gauge,
        poolName: gaugeData.pool || gauge,
        tokens: [],
        totalUsd,
        perVlCvx:
          liveVotesByGauge && liveVotes > 0
            ? totalUsd / liveVotes
            : gaugeData.perVlCvx || 0,
        maxPerVote: gaugeData.maxPerVote || false,
        votes: liveVotes,
        userVotes: 0,
        userReward: 0,
        optedOut,
      };

      if (gaugeData.tokens && Array.isArray(gaugeData.tokens)) {
        const tokenDetails = gaugeData.each || [];
        const tokenAddresses = gaugeData.tokens || [];

        tokenAddresses.forEach((tokenAddress, idx) => {
          const matchingTokenDetail = tokenDetails.find(
            (detail, detailIndex) =>
              detail.token === tokenAddress && detailIndex === idx
          );

          if (matchingTokenDetail) {
            bribeForDisplay.tokens.push({
              tokenAddress,
              pool: gauge,
              poolName: gaugeData.pool || gauge,
              dollarValue: matchingTokenDetail.amountUsd || 0,
              maxPerVote: matchingTokenDetail.maxPerVote || false,
              excluded: matchingTokenDetail.excluded || [],
              amount: matchingTokenDetail.amount || 0,
              symbol: matchingTokenDetail.symbol,
            });
          } else {
            bribeForDisplay.tokens.push({
              tokenAddress,
              pool: gauge,
              poolName: gaugeData.pool || gauge,
              dollarValue: 0,
              maxPerVote: false,
              amount: 0,
            });
          }
        });
      }

      if (currentUser?.uid && snapshotVotes?.[gauge]) {
        const userAddress = currentUser.uid.toLowerCase();
        if (snapshotVotes[gauge].votes?.[userAddress]) {
          bribeForDisplay.userVotes = snapshotVotes[gauge].votes[userAddress];

          const isExcluded = bribeForDisplay.tokens.some(
            (token) => token.excluded?.includes(userAddress)
          );

          if (!isExcluded && !optedOut) {
            bribeForDisplay.userReward =
              bribeForDisplay.perVlCvx * bribeForDisplay.userVotes;
          }
        }
      }

      bribesFormatted.push(bribeForDisplay);
    });

    bribesFormatted.sort((a, b) => b.totalUsd - a.totalUsd);
    return bribesFormatted;
  }, [
    bribesData,
    tokenPriceValue,
    snapshotVotes,
    currentUser,
    liveVotesByGauge,
    optedOutGauges,
  ]);

  return { bribesArray, snapshotChoices: gaugeAddresses, currentUser };
}
